import { Link } from "react-router";
import { Alerta, Button, DataList, MetricCard, Row, Select, Spinner, Stack, TextField } from "@/components";
import type { CobroListado } from "@/lib/api";
import { formatFecha, formatMoneda, formatNumero } from "@/lib/format";
import { ETIQUETA_MEDIO, nombreCuota } from "@/lib/ventas";
import { useCobrosTab } from "./useCobrosTab";

export function CobrosTab() {
  const t = useCobrosTab();
  const tot = t.totales;

  const columnas = [
    { clave: "fecha", titulo: "Fecha", render: (c: CobroListado) => formatFecha(c.fecha) },
    {
      clave: "venta",
      titulo: "Venta",
      render: (c: CobroListado) => (
        <Link to={`/ventas/${c.venta.id}`} className="text-primario underline-offset-2 hover:underline">
          {`Venta ${c.venta.numero}`}
        </Link>
      ),
    },
    { clave: "comprador", titulo: "Comprador", render: (c: CobroListado) => c.comprador.nombre },
    { clave: "cuota", titulo: "Cuota", render: (c: CobroListado) => nombreCuota(c.cuota) },
    {
      clave: "monto",
      titulo: "Monto",
      render: (c: CobroListado) => (
        <Stack espacio={1}>
          <span>{formatMoneda(c.monto, c.moneda)}</span>
          {c.tipo_cambio && <span className="text-chico text-texto-suave">{`TC ${formatNumero(c.tipo_cambio)}`}</span>}
        </Stack>
      ),
    },
    { clave: "usd", titulo: "USD", render: (c: CobroListado) => formatMoneda(c.monto_usd, "USD") },
    { clave: "medio", titulo: "Medio", render: (c: CobroListado) => ETIQUETA_MEDIO[c.medio] },
    {
      clave: "recibos",
      titulo: "Recibos",
      render: (c: CobroListado) =>
        c.adjuntos.length === 0 ? (
          "—"
        ) : (
          <Stack espacio={1}>
            {c.adjuntos.map((a) => (
              <a key={a.id} href={t.rutaDescarga(a)} className="truncate text-primario underline-offset-2 hover:underline" download>
                {a.nombre}
              </a>
            ))}
          </Stack>
        ),
    },
  ];

  return (
    <Stack espacio={6}>
      <Stack espacio={1}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Cobranzas</h1>
        <p className="text-chico text-texto-suave">Los cobros se cargan desde la ficha de cada venta.</p>
      </Stack>

      {tot && (
        <div className="grid gap-3 sm:grid-cols-3">
          <MetricCard titulo="En pesos" valor={formatMoneda(tot.ARS, "ARS")} />
          <MetricCard titulo="En dólares" valor={formatMoneda(tot.USD, "USD")} />
          <MetricCard titulo="Total en USD" valor={formatMoneda(tot.monto_usd, "USD")} detalle="Todos los cobros, pasados a USD" />
        </div>
      )}

      <Row envolver espacio={3} alinear="fin">
        <TextField etiqueta="Desde" type="date" value={t.filtros.desde} onChange={(e) => t.cambiarFiltro("desde", e.target.value)} />
        <TextField etiqueta="Hasta" type="date" value={t.filtros.hasta} onChange={(e) => t.cambiarFiltro("hasta", e.target.value)} />
        <Select etiqueta="Venta" opciones={t.opcionesVenta} valor={t.filtros.ventaId} onCambio={(v) => t.cambiarFiltro("ventaId", v)} className="min-w-52" />
        {t.hayFiltros && (
          <Button variante="fantasma" onClick={t.limpiarFiltros}>
            Limpiar filtros
          </Button>
        )}
      </Row>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && <DataList etiqueta="Cobros" columnas={columnas} filas={t.items} claveFila={(c) => c.id} vacio="No hay cobros para mostrar." />}
    </Stack>
  );
}
